import React, { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Add as AddIcon,
  AutoAwesome as AiIcon,
  Block as BlockIcon,
  CalendarToday as CalendarIcon,
  CurrencyRupee as CurrencyRupeeIcon,
  Dashboard as DashboardIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Logout as LogoutIcon,
  Payment as PaymentIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  TableChart as TableChartIcon,
  WbSunny as RashiIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '../contexts/AdminContext';
import AdminPinModal from '../components/AdminPinModal';
import Navbar from '../components/Layout/Navbar';
import Footer from '../components/Footer';
import apiClient, { kundaliApi, SavedChart, AiPredictionReport } from '../services/api';
import type { AiReportPdfRequest } from '../services/api';

interface Booking {
  id: string;
  name: string;
  email: string;
  phone: string;
  service: string;
  slot: string;
  paymentStatus: string;
  razorpayPaymentId?: string;
}

const MAROON = '#7B2D26';
const GOLD = '#C8922A';
const CREAM = '#FFFAF5';
const WARM_TONES = ['#7B2D26', '#C8922A', '#A0453D', '#DEAB4A', '#5C1F1A', '#D4C5B5', '#8B6914'];
const SUCCESS_GREEN = '#2E7D32';
const WARNING_ORANGE = '#ED6C02';
const ERROR_RED = '#D32F2F';
const BORDER_COLOR = '#F0E6DA';
const CARD_SHADOW = '0 2px 8px rgba(0,0,0,0.06)';

const servicePrices: Record<string, number> = {
  'Horoscope Reading': 1500,
  'Marriage Compatibility': 2000,
  'Vastu Consultation': 2500,
  'Pranic Healing': 2000,
  'Muhurta Selection': 1500,
  'Tantra Consultation': 2000,
  'Pendulum Dowsing': 1500,
};

const services = Object.keys(servicePrices);

const paymentStatusConfig: Record<string, { label: string; color: string; bg: string }> = {
  Paid: { label: 'Paid', color: SUCCESS_GREEN, bg: alpha(SUCCESS_GREEN, 0.12) },
  Pending: { label: 'Pending', color: WARNING_ORANGE, bg: alpha(WARNING_ORANGE, 0.12) },
  Failed: { label: 'Failed', color: ERROR_RED, bg: alpha(ERROR_RED, 0.12) },
};

const baseCardSx = {
  border: `1px solid ${BORDER_COLOR}`,
  boxShadow: CARD_SHADOW,
  borderRadius: 3,
  backgroundColor: '#FFFFFF',
};

const chartCardSx = {
  ...baseCardSx,
  height: '100%',
};

const filledButtonSx = {
  backgroundColor: MAROON,
  color: '#FFFFFF',
  textTransform: 'none',
  fontWeight: 700,
  borderRadius: 2,
  '&:hover': {
    backgroundColor: '#5C1F1A',
  },
};

const outlinedButtonSx = {
  color: MAROON,
  borderColor: alpha(MAROON, 0.35),
  textTransform: 'none',
  fontWeight: 700,
  borderRadius: 2,
  '&:hover': {
    borderColor: MAROON,
    backgroundColor: alpha(MAROON, 0.05),
  },
};

const formatCurrency = (value: number) => `₹${value.toLocaleString('en-IN')}`;

const getBookingDate = (slot: string) => {
  const datePart = slot?.split(' ')[0] || '';
  const parsed = dayjs(datePart);
  return parsed.isValid() ? parsed : null;
};

const AdminDashboard: React.FC = () => {
  const { isAdmin, isLoading: authLoading, logout } = useAdmin();
  const navigate = useNavigate();

  const [pinModalOpen, setPinModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [serviceFilter, setServiceFilter] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [blockedDates, setBlockedDates] = useState<string[]>([]);
  const [selectedBlockDate, setSelectedBlockDate] = useState<Dayjs | null>(null);
  const [blockDateLoading, setBlockDateLoading] = useState(false);
  const [rashiPhalStatus, setRashiPhalStatus] = useState<any>(null);
  const [regenerating, setRegenerating] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info',
  });
  const [savedCharts, setSavedCharts] = useState<SavedChart[]>([]);
  const [selectedSavedChart, setSelectedSavedChart] = useState<SavedChart | null>(null);
  const [aiReport, setAiReport] = useState<AiPredictionReport | null>(null);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [downloadingAiPdf, setDownloadingAiPdf] = useState(false);
  const [aiReportName, setAiReportName] = useState('');
  const [aiReportDob, setAiReportDob] = useState('');
  const [aiReportTob, setAiReportTob] = useState('');
  const [aiReportPob, setAiReportPob] = useState('');
  const [aiReportFocusAreas, setAiReportFocusAreas] = useState<string[]>(['general', 'remedies']);
  const [aiReportCharts, setAiReportCharts] = useState<string[]>(['D9']);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get('/admin/bookings');
      setBookings(response.data);
    } catch (err) {
      setError('Failed to load bookings data. Please check if the backend is running.');
      console.error('Error fetching bookings:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBlockedDates = async () => {
    try {
      const response = await apiClient.get('/admin/slot-config');
      setBlockedDates(response.data.blocked_dates || []);
    } catch (err) {
      console.error('Error fetching blocked dates:', err);
    }
  };

  const fetchRashiPhalStatus = async () => {
    try {
      const response = await apiClient.get('/rashi-phal');
      setRashiPhalStatus(response.data);
    } catch (err) {
      console.error('Error fetching rashi phal status:', err);
    }
  };

  const handleRegenerateRashiPhal = async () => {
    try {
      setRegenerating(true);
      await apiClient.post('/rashi-phal/regenerate');
      setSnackbar({
        open: true,
        message: 'Rashi Phal regenerated successfully!',
        severity: 'success',
      });
      await fetchRashiPhalStatus();
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to regenerate Rashi Phal',
        severity: 'error',
      });
    } finally {
      setRegenerating(false);
    }
  };

  const handleGenerateAiReport = async () => {
    if (!aiReportName || !aiReportDob || !aiReportTob || !aiReportPob) {
      setSnackbar({ open: true, message: 'Please fill all birth details', severity: 'warning' });
      return;
    }
    try {
      setGeneratingReport(true);
      setAiReport(null);
      const report = await kundaliApi.generateAiReport({
        name: aiReportName,
        date_of_birth: aiReportDob,
        time_of_birth: aiReportTob,
        place_of_birth: aiReportPob,
        focus_areas: aiReportFocusAreas,
        charts: aiReportCharts,
      });
      setAiReport(report);
      setSnackbar({ open: true, message: 'AI prediction report generated!', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.response?.data?.detail || 'Failed to generate AI report', severity: 'error' });
    } finally {
      setGeneratingReport(false);
    }
  };

  const handleLoadSavedForAiReport = (chart: SavedChart) => {
    setAiReportName(chart.name);
    setAiReportDob(chart.date_of_birth);
    setAiReportTob(chart.time_of_birth);
    setAiReportPob(chart.place_of_birth);
    setSelectedSavedChart(chart);
  };

  const handleDownloadAiReportPdf = async () => {
    if (!aiReport) {
      return;
    }

    const pdfRequest: AiReportPdfRequest = {
      name: aiReport.name || aiReportName,
      date_of_birth: aiReportDob,
      time_of_birth: aiReportTob,
      place_of_birth: aiReportPob,
      report: aiReport.report,
      focus_areas: aiReport.focus_areas || [],
      charts_used: aiReport.charts_used || [],
    };

    try {
      setDownloadingAiPdf(true);
      await kundaliApi.downloadAiReportPdf(pdfRequest);
      setSnackbar({ open: true, message: 'AI report PDF downloaded!', severity: 'success' });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.detail || 'Failed to download AI report PDF',
        severity: 'error',
      });
    } finally {
      setDownloadingAiPdf(false);
    }
  };

  const handleBlockDate = async () => {
    if (!selectedBlockDate) {
      return;
    }

    const dateStr = selectedBlockDate.format('YYYY-MM-DD');

    try {
      setBlockDateLoading(true);
      await apiClient.post(`/admin/blocked-dates?date=${dateStr}`);
      await fetchBlockedDates();
      setSelectedBlockDate(null);
      setSnackbar({
        open: true,
        message: `Date ${dateStr} has been blocked successfully`,
        severity: 'success',
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to block date',
        severity: 'error',
      });
    } finally {
      setBlockDateLoading(false);
    }
  };

  const handleUnblockDate = async (dateStr: string) => {
    try {
      setBlockDateLoading(true);
      await apiClient.delete(`/admin/blocked-dates/${dateStr}`);
      await fetchBlockedDates();
      setSnackbar({
        open: true,
        message: `Date ${dateStr} has been unblocked`,
        severity: 'success',
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to unblock date',
        severity: 'error',
      });
    } finally {
      setBlockDateLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchBookings();
      fetchBlockedDates();
      fetchRashiPhalStatus();
      kundaliApi.getSavedCharts().then(setSavedCharts).catch(() => {});
    }
  }, [isAdmin]);

  useEffect(() => {
    if (!authLoading && !isAdmin) {
      setPinModalOpen(true);
    }
  }, [authLoading, isAdmin]);

  useEffect(() => {
    setPage(0);
  }, [searchTerm, serviceFilter, paymentStatusFilter]);

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const filteredBookings = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    return bookings.filter((booking) => {
      const matchesSearch =
        !query ||
        booking.id.toLowerCase().includes(query) ||
        booking.name.toLowerCase().includes(query) ||
        booking.email.toLowerCase().includes(query) ||
        booking.phone.toLowerCase().includes(query);

      const matchesService = !serviceFilter || booking.service === serviceFilter;
      const matchesPayment = !paymentStatusFilter || booking.paymentStatus === paymentStatusFilter;

      return matchesSearch && matchesService && matchesPayment;
    });
  }, [bookings, paymentStatusFilter, searchTerm, serviceFilter]);

  const stats = useMemo(() => {
    const total = bookings.length;
    const paid = bookings.filter((booking) => booking.paymentStatus === 'Paid').length;
    const pending = bookings.filter((booking) => booking.paymentStatus === 'Pending').length;
    const failed = bookings.filter((booking) => booking.paymentStatus === 'Failed').length;
    const revenue = bookings.reduce((sum, booking) => {
      if (booking.paymentStatus !== 'Paid') {
        return sum;
      }
      return sum + (servicePrices[booking.service] || 0);
    }, 0);

    return { total, paid, pending, failed, revenue };
  }, [bookings]);

  const bookingsByServiceData = useMemo(() => {
    return services
      .map((service) => ({
        name: service,
        value: bookings.filter((booking) => booking.service === service).length,
      }))
      .filter((entry) => entry.value > 0);
  }, [bookings]);

  const bookingsOverTimeData = useMemo(() => {
    const monthlyMap = new Map<string, { monthKey: string; month: string; bookings: number; revenue: number }>();

    bookings.forEach((booking) => {
      const bookingDate = getBookingDate(booking.slot);
      if (!bookingDate) {
        return;
      }

      const monthKey = bookingDate.format('YYYY-MM');
      const current = monthlyMap.get(monthKey) || {
        monthKey,
        month: bookingDate.format('MMM YY'),
        bookings: 0,
        revenue: 0,
      };

      current.bookings += 1;
      if (booking.paymentStatus === 'Paid') {
        current.revenue += servicePrices[booking.service] || 0;
      }
      monthlyMap.set(monthKey, current);
    });

    return Array.from(monthlyMap.values()).sort((a, b) => a.monthKey.localeCompare(b.monthKey));
  }, [bookings]);

  const paymentStatusData = useMemo(() => {
    return [
      { name: 'Paid', value: stats.paid, color: SUCCESS_GREEN },
      { name: 'Pending', value: stats.pending, color: WARNING_ORANGE },
      { name: 'Failed', value: stats.failed, color: ERROR_RED },
    ].filter((entry) => entry.value > 0);
  }, [stats.failed, stats.paid, stats.pending]);

  const revenueByServiceData = useMemo(() => {
    return services
      .map((service) => {
        const paidBookings = bookings.filter(
          (booking) => booking.service === service && booking.paymentStatus === 'Paid'
        ).length;

        return {
          name: service,
          shortName: service.replace(' Consultation', '').replace(' Compatibility', ''),
          revenue: paidBookings * (servicePrices[service] || 0),
          paidBookings,
        };
      })
      .filter((entry) => entry.revenue > 0);
  }, [bookings]);

  const paginatedBookings = useMemo(
    () => filteredBookings.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredBookings, page, rowsPerPage]
  );

  const exportToCSV = () => {
    const headers = [
      'Booking ID',
      'Name',
      'Email',
      'Phone',
      'Service',
      'Slot',
      'Payment Status',
      'Razorpay Payment ID',
    ];

    const escapeCell = (value: string) => `"${value.replace(/"/g, '""')}"`;

    const csvContent = [
      headers.join(','),
      ...filteredBookings.map((booking) =>
        [
          booking.id,
          booking.name,
          booking.email,
          booking.phone,
          booking.service,
          booking.slot,
          booking.paymentStatus,
          booking.razorpayPaymentId || 'N/A',
        ]
          .map((value) => escapeCell(String(value)))
          .join(',')
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `bookings_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getPaymentChipSx = (status: string) => {
    const config = paymentStatusConfig[status] || {
      label: status,
      color: MAROON,
      bg: alpha(MAROON, 0.12),
    };

    return {
      fontWeight: 700,
      color: config.color,
      backgroundColor: config.bg,
      border: `1px solid ${alpha(config.color, 0.2)}`,
    };
  };

  const statsCards = [
    {
      title: 'Total Bookings',
      value: stats.total,
      icon: <PersonIcon sx={{ color: MAROON }} />,
      gradient: `linear-gradient(135deg, ${alpha(MAROON, 0.12)} 0%, ${alpha(GOLD, 0.08)} 100%)`,
    },
    {
      title: 'Paid',
      value: stats.paid,
      icon: <PaymentIcon sx={{ color: SUCCESS_GREEN }} />,
      gradient: `linear-gradient(135deg, ${alpha(SUCCESS_GREEN, 0.12)} 0%, ${alpha('#A5D6A7', 0.2)} 100%)`,
    },
    {
      title: 'Pending',
      value: stats.pending,
      icon: <CalendarIcon sx={{ color: WARNING_ORANGE }} />,
      gradient: `linear-gradient(135deg, ${alpha(WARNING_ORANGE, 0.12)} 0%, ${alpha(GOLD, 0.14)} 100%)`,
    },
    {
      title: 'Failed',
      value: stats.failed,
      icon: <BlockIcon sx={{ color: ERROR_RED }} />,
      gradient: `linear-gradient(135deg, ${alpha(ERROR_RED, 0.1)} 0%, ${alpha('#E57373', 0.14)} 100%)`,
    },
    {
      title: 'Revenue',
      value: formatCurrency(stats.revenue),
      icon: <CurrencyRupeeIcon sx={{ color: GOLD }} />,
      gradient: `linear-gradient(135deg, ${alpha(GOLD, 0.16)} 0%, ${alpha('#DEAB4A', 0.12)} 100%)`,
    },
  ];

  const renderChartEmptyState = (message: string) => (
    <Box
      sx={{
        height: 280,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        color: alpha(MAROON, 0.72),
        px: 2,
      }}
    >
      <Typography variant="body2">{message}</Typography>
    </Box>
  );

  const bookingsErrorBanner = error ? (
    <Alert
      severity="error"
      sx={{
        mb: 3,
        ...baseCardSx,
        color: ERROR_RED,
        alignItems: 'center',
      }}
      action={
        <Button variant="outlined" size="small" onClick={fetchBookings} sx={outlinedButtonSx}>
          Retry
        </Button>
      }
    >
      {error}
    </Alert>
  ) : null;

  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress sx={{ color: MAROON }} />
      </Box>
    );
  }

  if (!isAdmin) {
    return (
      <>
        <Navbar />
        <Box
          sx={{
            minHeight: '60vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 3,
            backgroundColor: CREAM,
          }}
        >
          <Typography variant="h6" sx={{ color: MAROON }}>
            Admin access required
          </Typography>
        </Box>
        <AdminPinModal
          open={pinModalOpen}
          onClose={() => {
            setPinModalOpen(false);
            navigate('/');
          }}
          onSuccess={() => setPinModalOpen(false)}
        />
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <Box sx={{ minHeight: '100vh', backgroundColor: CREAM, py: { xs: 3, md: 5 }, px: { xs: 2, md: 3 } }}>
        <Box sx={{ maxWidth: 1440, mx: 'auto' }}>
          <Box
            sx={{
              ...baseCardSx,
              mb: 3,
              px: { xs: 2.5, md: 4 },
              py: { xs: 2.5, md: 3 },
              background: `linear-gradient(135deg, ${alpha(CREAM, 0.95)} 0%, ${alpha(GOLD, 0.08)} 100%)`,
            }}
          >
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
            >
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    color: MAROON,
                    fontWeight: 800,
                    fontSize: { xs: '1.75rem', md: '2.25rem' },
                    mb: 0.75,
                  }}
                >
                  Admin Dashboard
                </Typography>
                <Typography variant="body1" sx={{ color: alpha(MAROON, 0.78) }}>
                  Manage bookings, monitor revenue, and oversee daily Rashi Phal updates.
                </Typography>
              </Box>
              <Button
                variant="outlined"
                startIcon={<LogoutIcon />}
                onClick={() => {
                  logout();
                  navigate('/');
                }}
                sx={outlinedButtonSx}
              >
                Logout
              </Button>
            </Stack>
          </Box>

          <Box sx={{ ...baseCardSx, mb: 3, overflow: 'hidden' }}>
            <Tabs
              value={activeTab}
              onChange={(_event, value) => setActiveTab(value)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                px: { xs: 1, md: 2 },
                '& .MuiTabs-indicator': {
                  backgroundColor: GOLD,
                  height: 3,
                },
                '& .MuiTab-root': {
                  textTransform: 'none',
                  minHeight: 68,
                  fontWeight: 700,
                  color: alpha(MAROON, 0.72),
                },
                '& .Mui-selected': {
                  color: MAROON,
                },
              }}
            >
              <Tab icon={<DashboardIcon />} iconPosition="start" label="Overview" />
              <Tab icon={<TableChartIcon />} iconPosition="start" label="Bookings" />
              <Tab icon={<RashiIcon />} iconPosition="start" label="Rashi Phal" />
              <Tab icon={<AiIcon />} iconPosition="start" label="AI Report" />
            </Tabs>
          </Box>

          {activeTab === 0 && (
            <Box>
              {bookingsErrorBanner}
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                  <CircularProgress sx={{ color: MAROON }} />
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, minmax(0, 1fr))',
                        lg: 'repeat(5, minmax(0, 1fr))',
                      },
                      gap: 2,
                      mb: 3,
                    }}
                  >
                    {statsCards.map((card) => (
                      <Card key={card.title} sx={{ ...baseCardSx, background: card.gradient }}>
                        <CardContent>
                          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                            <Box>
                              <Typography variant="body2" sx={{ color: alpha(MAROON, 0.72), mb: 1 }}>
                                {card.title}
                              </Typography>
                              <Typography variant="h5" sx={{ fontWeight: 800, color: MAROON }}>
                                {card.value}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                width: 44,
                                height: 44,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: alpha('#FFFFFF', 0.5),
                                border: `1px solid ${alpha(MAROON, 0.08)}`,
                              }}
                            >
                              {card.icon}
                            </Box>
                          </Stack>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' },
                      gap: 3,
                    }}
                  >
                    <Card sx={chartCardSx}>
                      <CardContent>
                        <Typography variant="h6" sx={{ color: MAROON, fontWeight: 800, mb: 0.5 }}>
                          Bookings by Service
                        </Typography>
                        <Typography variant="body2" sx={{ color: alpha(MAROON, 0.72), mb: 2 }}>
                          Distribution of consultations across service categories.
                        </Typography>
                        {bookingsByServiceData.length ? (
                          <Box sx={{ height: 320 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie
                                  data={bookingsByServiceData}
                                  dataKey="value"
                                  nameKey="name"
                                  cx="50%"
                                  cy="50%"
                                  outerRadius={105}
                                >
                                  {bookingsByServiceData.map((entry, index) => (
                                    <Cell key={entry.name} fill={WARM_TONES[index % WARM_TONES.length]} />
                                  ))}
                                </Pie>
                                <Legend />
                                <RechartsTooltip contentStyle={{ borderRadius: 12, border: `1px solid ${BORDER_COLOR}` }} />
                              </PieChart>
                            </ResponsiveContainer>
                          </Box>
                        ) : (
                          renderChartEmptyState('Bookings will appear here once services are booked.')
                        )}
                      </CardContent>
                    </Card>

                    <Card sx={chartCardSx}>
                      <CardContent>
                        <Typography variant="h6" sx={{ color: MAROON, fontWeight: 800, mb: 0.5 }}>
                          Bookings Over Time
                        </Typography>
                        <Typography variant="body2" sx={{ color: alpha(MAROON, 0.72), mb: 2 }}>
                          Monthly booking volume based on scheduled slots.
                        </Typography>
                        {bookingsOverTimeData.length ? (
                          <Box sx={{ height: 320 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <AreaChart data={bookingsOverTimeData}>
                                <defs>
                                  <linearGradient id="bookingsGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={GOLD} stopOpacity={0.8} />
                                    <stop offset="95%" stopColor={GOLD} stopOpacity={0.08} />
                                  </linearGradient>
                                </defs>
                                <CartesianGrid stroke={alpha(MAROON, 0.1)} strokeDasharray="3 3" />
                                <XAxis dataKey="month" stroke={alpha(MAROON, 0.7)} />
                                <YAxis allowDecimals={false} stroke={alpha(MAROON, 0.7)} />
                                <RechartsTooltip contentStyle={{ borderRadius: 12, border: `1px solid ${BORDER_COLOR}` }} />
                                <Area type="monotone" dataKey="bookings" stroke={MAROON} fill="url(#bookingsGradient)" strokeWidth={3} />
                              </AreaChart>
                            </ResponsiveContainer>
                          </Box>
                        ) : (
                          renderChartEmptyState('Monthly trends will appear after booking data is available.')
                        )}
                      </CardContent>
                    </Card>

                    <Card sx={chartCardSx}>
                      <CardContent>
                        <Typography variant="h6" sx={{ color: MAROON, fontWeight: 800, mb: 0.5 }}>
                          Payment Status
                        </Typography>
                        <Typography variant="body2" sx={{ color: alpha(MAROON, 0.72), mb: 2 }}>
                          Paid, pending, and failed payments at a glance.
                        </Typography>
                        {paymentStatusData.length ? (
                          <Box sx={{ height: 320 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie
                                  data={paymentStatusData}
                                  dataKey="value"
                                  nameKey="name"
                                  innerRadius={68}
                                  outerRadius={108}
                                  paddingAngle={4}
                                >
                                  {paymentStatusData.map((entry) => (
                                    <Cell key={entry.name} fill={entry.color} />
                                  ))}
                                </Pie>
                                <Legend />
                                <RechartsTooltip contentStyle={{ borderRadius: 12, border: `1px solid ${BORDER_COLOR}` }} />
                              </PieChart>
                            </ResponsiveContainer>
                          </Box>
                        ) : (
                          renderChartEmptyState('Payment status insights will appear once bookings are fetched.')
                        )}
                      </CardContent>
                    </Card>

                    <Card sx={chartCardSx}>
                      <CardContent>
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          justifyContent="space-between"
                          spacing={1}
                          sx={{ mb: 2 }}
                        >
                          <Box>
                            <Typography variant="h6" sx={{ color: MAROON, fontWeight: 800, mb: 0.5 }}>
                              Revenue Overview
                            </Typography>
                            <Typography variant="body2" sx={{ color: alpha(MAROON, 0.72) }}>
                              Estimated revenue from paid bookings by service.
                            </Typography>
                          </Box>
                          <Chip
                            icon={<CurrencyRupeeIcon sx={{ color: `${GOLD} !important` }} />}
                            label={formatCurrency(stats.revenue)}
                            sx={{
                              alignSelf: 'flex-start',
                              fontWeight: 800,
                              color: MAROON,
                              backgroundColor: alpha(GOLD, 0.15),
                              border: `1px solid ${alpha(GOLD, 0.25)}`,
                            }}
                          />
                        </Stack>
                        {revenueByServiceData.length ? (
                          <Box sx={{ height: 320 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={revenueByServiceData}>
                                <CartesianGrid stroke={alpha(MAROON, 0.1)} strokeDasharray="3 3" />
                                <XAxis dataKey="shortName" stroke={alpha(MAROON, 0.7)} interval={0} angle={-12} textAnchor="end" height={70} />
                                <YAxis stroke={alpha(MAROON, 0.7)} tickFormatter={(value) => `₹${Number(value) / 1000}k`} />
                                <RechartsTooltip contentStyle={{ borderRadius: 12, border: `1px solid ${BORDER_COLOR}` }} />
                                <Bar dataKey="revenue" fill={GOLD} radius={[8, 8, 0, 0]} />
                              </BarChart>
                            </ResponsiveContainer>
                          </Box>
                        ) : (
                          renderChartEmptyState('Revenue will be estimated as soon as paid bookings are present.')
                        )}
                      </CardContent>
                    </Card>
                  </Box>
                </>
              )}
            </Box>
          )}

          {activeTab === 1 && (
            <Box>
              {bookingsErrorBanner}
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                  <CircularProgress sx={{ color: MAROON }} />
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', xl: '1.4fr 1fr' },
                      gap: 3,
                      mb: 3,
                    }}
                  >
                    <Card sx={baseCardSx}>
                      <CardContent>
                        <Stack
                          direction={{ xs: 'column', md: 'row' }}
                          justifyContent="space-between"
                          spacing={2}
                          sx={{ mb: 3 }}
                        >
                          <Box>
                            <Typography variant="h6" sx={{ color: MAROON, fontWeight: 800, mb: 0.5 }}>
                              Booking Filters
                            </Typography>
                            <Typography variant="body2" sx={{ color: alpha(MAROON, 0.72) }}>
                              Search by booking ID, name, email, or phone and refine by service or payment status.
                            </Typography>
                          </Box>
                          <Button variant="contained" startIcon={<DownloadIcon />} onClick={exportToCSV} sx={filledButtonSx}>
                            Export CSV
                          </Button>
                        </Stack>

                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: '1.6fr 1fr 1fr' },
                            gap: 2,
                          }}
                        >
                          <TextField
                            fullWidth
                            label="Search bookings"
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon sx={{ color: alpha(MAROON, 0.65) }} />
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                '& fieldset': { borderColor: BORDER_COLOR },
                                '&:hover fieldset': { borderColor: alpha(MAROON, 0.4) },
                                '&.Mui-focused fieldset': { borderColor: MAROON },
                              },
                              '& .MuiInputLabel-root.Mui-focused': { color: MAROON },
                            }}
                          />

                          <FormControl fullWidth>
                            <InputLabel sx={{ '&.Mui-focused': { color: MAROON } }}>Service</InputLabel>
                            <Select
                              value={serviceFilter}
                              label="Service"
                              onChange={(event) => setServiceFilter(event.target.value)}
                              sx={{
                                borderRadius: 2,
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: BORDER_COLOR },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: alpha(MAROON, 0.4) },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: MAROON },
                              }}
                            >
                              <MenuItem value="">All Services</MenuItem>
                              {services.map((service) => (
                                <MenuItem key={service} value={service}>
                                  {service}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>

                          <FormControl fullWidth>
                            <InputLabel sx={{ '&.Mui-focused': { color: MAROON } }}>Payment Status</InputLabel>
                            <Select
                              value={paymentStatusFilter}
                              label="Payment Status"
                              onChange={(event) => setPaymentStatusFilter(event.target.value)}
                              sx={{
                                borderRadius: 2,
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: BORDER_COLOR },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: alpha(MAROON, 0.4) },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: MAROON },
                              }}
                            >
                              <MenuItem value="">All Status</MenuItem>
                              <MenuItem value="Paid">Paid</MenuItem>
                              <MenuItem value="Pending">Pending</MenuItem>
                              <MenuItem value="Failed">Failed</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                      </CardContent>
                    </Card>

                    <Card sx={baseCardSx}>
                      <CardContent>
                        <Typography variant="h6" sx={{ color: MAROON, fontWeight: 800, mb: 0.5 }}>
                          Blocked Dates Management
                        </Typography>
                        <Typography variant="body2" sx={{ color: alpha(MAROON, 0.72), mb: 3 }}>
                          Keep unavailable consultation dates out of the booking calendar.
                        </Typography>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
                          <DatePicker
                            label="Select Date to Block"
                            value={selectedBlockDate}
                            onChange={setSelectedBlockDate}
                            minDate={dayjs()}
                            sx={{
                              flex: 1,
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                '& fieldset': { borderColor: BORDER_COLOR },
                                '&:hover fieldset': { borderColor: alpha(MAROON, 0.4) },
                                '&.Mui-focused fieldset': { borderColor: MAROON },
                              },
                              '& .MuiInputLabel-root.Mui-focused': { color: MAROON },
                            }}
                          />
                          <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleBlockDate}
                            disabled={!selectedBlockDate || blockDateLoading}
                            sx={{ ...filledButtonSx, minWidth: 160 }}
                          >
                            {blockDateLoading ? 'Saving...' : 'Block Date'}
                          </Button>
                        </Stack>

                        <Box sx={{ mt: 3 }}>
                          <Typography variant="subtitle2" sx={{ color: alpha(MAROON, 0.78), mb: 1.5, fontWeight: 700 }}>
                            Currently blocked ({blockedDates.length})
                          </Typography>
                          {blockedDates.length ? (
                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                              {[...blockedDates].sort().map((date) => (
                                <Chip
                                  key={date}
                                  label={dayjs(date).format('MMM DD, YYYY')}
                                  onDelete={() => handleUnblockDate(date)}
                                  deleteIcon={<DeleteIcon />}
                                  sx={{
                                    fontWeight: 700,
                                    color: MAROON,
                                    backgroundColor: alpha(GOLD, 0.14),
                                    border: `1px solid ${alpha(GOLD, 0.25)}`,
                                    '& .MuiChip-deleteIcon': {
                                      color: MAROON,
                                      '&:hover': { color: '#5C1F1A' },
                                    },
                                  }}
                                />
                              ))}
                            </Stack>
                          ) : (
                            <Typography variant="body2" sx={{ color: alpha(MAROON, 0.6), fontStyle: 'italic' }}>
                              No dates are currently blocked.
                            </Typography>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Box>

                  <Card sx={baseCardSx}>
                    <CardContent>
                      <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        justifyContent="space-between"
                        spacing={1}
                        sx={{ mb: 2 }}
                      >
                        <Box>
                          <Typography variant="h6" sx={{ color: MAROON, fontWeight: 800, mb: 0.5 }}>
                            Bookings Table
                          </Typography>
                          <Typography variant="body2" sx={{ color: alpha(MAROON, 0.72) }}>
                            Showing {filteredBookings.length} booking{filteredBookings.length === 1 ? '' : 's'} after filters.
                          </Typography>
                        </Box>
                        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchBookings} sx={outlinedButtonSx}>
                          Refresh
                        </Button>
                      </Stack>

                      <TableContainer
                        component={Paper}
                        sx={{
                          border: `1px solid ${BORDER_COLOR}`,
                          borderRadius: 2,
                          boxShadow: 'none',
                          maxHeight: 640,
                          '& .MuiTableCell-head': {
                            backgroundColor: alpha(GOLD, 0.14),
                            color: MAROON,
                            fontWeight: 800,
                            borderBottom: `1px solid ${BORDER_COLOR}`,
                          },
                          '& .MuiTableCell-body': {
                            borderBottom: `1px solid ${alpha(BORDER_COLOR, 0.9)}`,
                          },
                          '& .MuiTableRow-root:hover': {
                            backgroundColor: alpha(GOLD, 0.05),
                          },
                        }}
                      >
                        <Table stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell>Booking ID</TableCell>
                              <TableCell>Name</TableCell>
                              <TableCell>Email</TableCell>
                              <TableCell>Phone</TableCell>
                              <TableCell>Service</TableCell>
                              <TableCell>Slot</TableCell>
                              <TableCell>Payment Status</TableCell>
                              <TableCell>Payment ID</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {paginatedBookings.length ? (
                              paginatedBookings.map((booking) => (
                                <TableRow key={booking.id} hover>
                                  <TableCell sx={{ color: MAROON, fontWeight: 700 }}>{booking.id}</TableCell>
                                  <TableCell sx={{ color: '#4A2C2A', fontWeight: 600 }}>{booking.name}</TableCell>
                                  <TableCell sx={{ color: '#6F4E43' }}>{booking.email}</TableCell>
                                  <TableCell sx={{ color: '#6F4E43' }}>{booking.phone}</TableCell>
                                  <TableCell sx={{ color: '#4A2C2A' }}>{booking.service}</TableCell>
                                  <TableCell sx={{ color: '#6F4E43', minWidth: 180 }}>{booking.slot}</TableCell>
                                  <TableCell>
                                    <Chip label={booking.paymentStatus} size="small" sx={getPaymentChipSx(booking.paymentStatus)} />
                                  </TableCell>
                                  <TableCell>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: booking.razorpayPaymentId ? '#6F4E43' : alpha(MAROON, 0.5),
                                        fontFamily: booking.razorpayPaymentId ? 'monospace' : 'inherit',
                                        fontStyle: booking.razorpayPaymentId ? 'normal' : 'italic',
                                      }}
                                    >
                                      {booking.razorpayPaymentId || 'N/A'}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={8} sx={{ py: 6 }}>
                                  <Typography align="center" sx={{ color: alpha(MAROON, 0.7) }}>
                                    No bookings match the current filters.
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      <TablePagination
                        component="div"
                        rowsPerPageOptions={[5, 10, 25]}
                        count={filteredBookings.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{
                          mt: 1,
                          color: alpha(MAROON, 0.8),
                          '& .MuiTablePagination-selectIcon': { color: MAROON },
                        }}
                      />
                    </CardContent>
                  </Card>
                </>
              )}
            </Box>
          )}

          {activeTab === 2 && (
            <Card sx={baseCardSx}>
              <CardContent>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  justifyContent="space-between"
                  spacing={2}
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                  sx={{ mb: 3 }}
                >
                  <Box>
                    <Typography variant="h5" sx={{ color: MAROON, fontWeight: 800, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <RashiIcon /> Daily Rashi Phal Management
                    </Typography>
                    <Typography variant="body2" sx={{ color: alpha(MAROON, 0.72) }}>
                      Review update status, inspect current transits, and trigger regeneration when needed.
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={regenerating ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                    onClick={handleRegenerateRashiPhal}
                    disabled={regenerating}
                    sx={filledButtonSx}
                  >
                    {regenerating ? 'Regenerating...' : "Regenerate Today's Rashi Phal"}
                  </Button>
                </Stack>

                {rashiPhalStatus ? (
                  <>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
                        gap: 2,
                        mb: 3,
                      }}
                    >
                      <Card sx={{ ...baseCardSx, background: `linear-gradient(135deg, ${alpha(GOLD, 0.12)} 0%, ${alpha(CREAM, 0.92)} 100%)` }}>
                        <CardContent>
                          <Typography variant="body2" sx={{ color: alpha(MAROON, 0.72), mb: 1 }}>
                            Last Updated
                          </Typography>
                          <Typography variant="h6" sx={{ color: MAROON, fontWeight: 800 }}>
                            {rashiPhalStatus.lastUpdated || 'Not available'}
                          </Typography>
                        </CardContent>
                      </Card>
                      <Card sx={{ ...baseCardSx, background: `linear-gradient(135deg, ${alpha(MAROON, 0.1)} 0%, ${alpha(CREAM, 0.95)} 100%)` }}>
                        <CardContent>
                          <Typography variant="body2" sx={{ color: alpha(MAROON, 0.72), mb: 1 }}>
                            Source
                          </Typography>
                          <Chip
                            label={rashiPhalStatus.source || 'unknown'}
                            sx={{
                              fontWeight: 700,
                              color: rashiPhalStatus.source === 'ai_generated' ? SUCCESS_GREEN : WARNING_ORANGE,
                              backgroundColor:
                                rashiPhalStatus.source === 'ai_generated'
                                  ? alpha(SUCCESS_GREEN, 0.12)
                                  : alpha(WARNING_ORANGE, 0.12),
                            }}
                          />
                        </CardContent>
                      </Card>
                      <Card sx={{ ...baseCardSx, background: `linear-gradient(135deg, ${alpha('#DEAB4A', 0.12)} 0%, ${alpha(CREAM, 0.95)} 100%)` }}>
                        <CardContent>
                          <Typography variant="body2" sx={{ color: alpha(MAROON, 0.72), mb: 1 }}>
                            Rashis Generated
                          </Typography>
                          <Typography variant="h6" sx={{ color: MAROON, fontWeight: 800 }}>
                            {rashiPhalStatus.rashis?.length || 0} / 12
                          </Typography>
                        </CardContent>
                      </Card>
                    </Box>

                    {rashiPhalStatus.transits && rashiPhalStatus.transits.length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" sx={{ color: MAROON, fontWeight: 800, mb: 1.5 }}>
                          Current Transits
                        </Typography>
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                          {rashiPhalStatus.transits.map((transit: any, index: number) => (
                            <Chip
                              key={`${transit.planet}-${transit.sign}-${index}`}
                              label={`${transit.planet} in ${transit.sign}${
                                typeof transit.degree === 'number' ? ` (${transit.degree.toFixed(1)}°)` : ''
                              }`}
                              variant="outlined"
                              sx={{
                                color: MAROON,
                                borderColor: alpha(MAROON, 0.22),
                                backgroundColor: alpha(CREAM, 0.8),
                              }}
                            />
                          ))}
                        </Stack>
                      </Box>
                    )}

                    <Alert severity="info" sx={{ ...baseCardSx, color: MAROON }}>
                      Regenerating Rashi Phal calls the OpenAI API (~₹1-2 per generation). The system auto-generates on the first request each day.
                    </Alert>
                  </>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress sx={{ color: MAROON }} />
                  </Box>
                )}
              </CardContent>
            </Card>
          )}
        </Box>

          {activeTab === 3 && (
            <Box>
              <Card sx={{ ...baseCardSx, mb: 3 }}>
                <CardContent>
                  <Typography variant="h5" sx={{ color: MAROON, fontWeight: 800, mb: 0.5 }}>
                    <AiIcon sx={{ mr: 1, verticalAlign: 'middle', color: GOLD }} />
                    AI Kundali Prediction Report
                  </Typography>
                  <Typography variant="body2" sx={{ color: alpha(MAROON, 0.65), mb: 3 }}>
                    Generate a detailed AI-powered prediction report for astrologer reference. Uses GPT-4.1 Mini for comprehensive analysis.
                  </Typography>

                  {savedCharts.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" sx={{ color: MAROON, fontWeight: 700, mb: 1 }}>
                        Quick Load from Saved Charts
                      </Typography>
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        {savedCharts.map((chart) => (
                          <Chip
                            key={chart._id}
                            label={chart.name}
                            variant={selectedSavedChart?._id === chart._id ? 'filled' : 'outlined'}
                            onClick={() => handleLoadSavedForAiReport(chart)}
                            sx={{
                              color: selectedSavedChart?._id === chart._id ? '#fff' : MAROON,
                              backgroundColor: selectedSavedChart?._id === chart._id ? MAROON : 'transparent',
                              borderColor: alpha(MAROON, 0.3),
                              '&:hover': { backgroundColor: selectedSavedChart?._id === chart._id ? MAROON : alpha(MAROON, 0.08) },
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}

                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
                    <TextField
                      label="Full Name"
                      value={aiReportName}
                      onChange={(e) => setAiReportName(e.target.value)}
                      fullWidth
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <TextField
                      label="Date of Birth"
                      value={aiReportDob}
                      onChange={(e) => setAiReportDob(e.target.value)}
                      placeholder="DD/MM/YYYY"
                      fullWidth
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <TextField
                      label="Time of Birth"
                      value={aiReportTob}
                      onChange={(e) => setAiReportTob(e.target.value)}
                      placeholder="HH:MM (24h)"
                      fullWidth
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <TextField
                      label="Place of Birth"
                      value={aiReportPob}
                      onChange={(e) => setAiReportPob(e.target.value)}
                      fullWidth
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Box>

                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Prediction Focus Areas</InputLabel>
                      <Select
                        multiple
                        value={aiReportFocusAreas}
                        onChange={(e) => setAiReportFocusAreas(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value as string[])}
                        label="Prediction Focus Areas"
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(selected as string[]).map((v) => (
                              <Chip key={v} label={v.charAt(0).toUpperCase() + v.slice(1)} size="small" sx={{ backgroundColor: alpha(GOLD, 0.15), color: MAROON, fontWeight: 600 }} />
                            ))}
                          </Box>
                        )}
                        sx={{ borderRadius: 2 }}
                      >
                        {[
                          { value: 'general', label: 'General Overview' },
                          { value: 'career', label: 'Career & Profession' },
                          { value: 'finance', label: 'Finance & Wealth' },
                          { value: 'marriage', label: 'Marriage & Relationships' },
                          { value: 'health', label: 'Health & Longevity' },
                          { value: 'education', label: 'Education & Learning' },
                          { value: 'children', label: 'Children & Progeny' },
                          { value: 'spirituality', label: 'Spirituality & Dharma' },
                          { value: 'remedies', label: 'Remedies & Recommendations' },
                        ].map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            <Checkbox checked={aiReportFocusAreas.indexOf(opt.value) > -1} size="small" sx={{ color: MAROON, '&.Mui-checked': { color: MAROON } }} />
                            <ListItemText primary={opt.label} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl fullWidth size="small">
                      <InputLabel>Divisional Charts to Analyze</InputLabel>
                      <Select
                        multiple
                        value={aiReportCharts}
                        onChange={(e) => setAiReportCharts(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value as string[])}
                        label="Divisional Charts to Analyze"
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(selected as string[]).map((v) => (
                              <Chip key={v} label={v} size="small" sx={{ backgroundColor: alpha(MAROON, 0.1), color: MAROON, fontWeight: 600 }} />
                            ))}
                          </Box>
                        )}
                        sx={{ borderRadius: 2 }}
                      >
                        {[
                          { value: 'D1', label: 'D1 — Rasi (Birth Chart)' },
                          { value: 'D2', label: 'D2 — Hora (Wealth)' },
                          { value: 'D3', label: 'D3 — Drekkana (Siblings)' },
                          { value: 'D4', label: 'D4 — Chaturthamsa (Property)' },
                          { value: 'D6', label: 'D6 — Shashthamsa (Health)' },
                          { value: 'D7', label: 'D7 — Saptamsa (Children)' },
                          { value: 'D8', label: 'D8 — Ashtamsa (Longevity)' },
                          { value: 'D9', label: 'D9 — Navamsa (Marriage)' },
                          { value: 'D10', label: 'D10 — Dasamsa (Career)' },
                          { value: 'D12', label: 'D12 — Dwadasamsa (Parents)' },
                          { value: 'D16', label: 'D16 — Shodasamsa (Comforts)' },
                          { value: 'D20', label: 'D20 — Vimsamsa (Spirituality)' },
                          { value: 'D24', label: 'D24 — Chaturvimsamsa (Education)' },
                          { value: 'D27', label: 'D27 — Saptavimsamsa (Strength)' },
                          { value: 'D30', label: 'D30 — Trimsamsa (Misfortunes)' },
                          { value: 'D40', label: 'D40 — Khavedamsa (Auspiciousness)' },
                          { value: 'D45', label: 'D45 — Akshavedamsa (Character)' },
                          { value: 'D60', label: 'D60 — Shashtiamsa (Past Karma)' },
                        ].map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            <Checkbox checked={aiReportCharts.indexOf(opt.value) > -1} size="small" sx={{ color: GOLD, '&.Mui-checked': { color: GOLD } }} />
                            <ListItemText primary={opt.label} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  <Button
                    variant="contained"
                    startIcon={generatingReport ? <CircularProgress size={18} color="inherit" /> : <AiIcon />}
                    onClick={handleGenerateAiReport}
                    disabled={generatingReport}
                    sx={{
                      backgroundColor: MAROON,
                      '&:hover': { backgroundColor: alpha(MAROON, 0.85) },
                      borderRadius: 2,
                      fontWeight: 700,
                      px: 4,
                    }}
                  >
                    {generatingReport ? 'Generating Report...' : 'Generate AI Report'}
                  </Button>
                </CardContent>
              </Card>

              {aiReport && (
                <Card sx={{ ...baseCardSx }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h5" sx={{ color: MAROON, fontWeight: 800 }}>
                        Prediction Report — {aiReport.name}
                      </Typography>
                      <Chip label="AI Generated" icon={<AiIcon />} sx={{ color: GOLD, borderColor: GOLD }} variant="outlined" />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                      <Chip label={`DOB: ${aiReportDob}`} size="small" variant="outlined" sx={{ borderColor: alpha(MAROON, 0.2) }} />
                      <Chip label={`TOB: ${aiReportTob}`} size="small" variant="outlined" sx={{ borderColor: alpha(MAROON, 0.2) }} />
                      <Chip label={`POB: ${aiReportPob}`} size="small" variant="outlined" sx={{ borderColor: alpha(MAROON, 0.2) }} />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ color: alpha(MAROON, 0.6), fontWeight: 700 }}>Focus:</Typography>
                      {aiReport.focus_areas?.map((area) => (
                        <Chip key={area} label={area.charAt(0).toUpperCase() + area.slice(1)} size="small" sx={{ backgroundColor: alpha(GOLD, 0.15), color: MAROON, fontWeight: 600 }} />
                      ))}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ color: alpha(MAROON, 0.6), fontWeight: 700 }}>Charts:</Typography>
                      {aiReport.charts_used?.map((chart) => (
                        <Chip key={chart} label={chart} size="small" sx={{ backgroundColor: alpha(MAROON, 0.1), color: MAROON, fontWeight: 600 }} />
                      ))}
                    </Box>
                    <Box
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        backgroundColor: alpha(CREAM, 0.6),
                        border: `1px solid ${alpha(GOLD, 0.2)}`,
                        fontFamily: '"Georgia", serif',
                        fontSize: '0.95rem',
                        lineHeight: 1.8,
                        color: alpha(MAROON, 0.9),
                        '& h1, & h2, & h3, & h4': { color: MAROON, fontWeight: 700, mt: 2, mb: 1 },
                        '& h2': { fontSize: '1.3rem', borderBottom: `1px solid ${alpha(GOLD, 0.3)}`, pb: 0.5 },
                        '& h3': { fontSize: '1.1rem' },
                        '& p': { mb: 1.5 },
                        '& ul, & ol': { pl: 3, mb: 1.5 },
                        '& li': { mb: 0.5 },
                        '& strong': { color: MAROON, fontWeight: 700 },
                        '& hr': { border: 'none', borderTop: `1px solid ${alpha(GOLD, 0.25)}`, my: 2 },
                      }}
                    >
                      <ReactMarkdown>{aiReport.report}</ReactMarkdown>
                    </Box>
                    <Button
                      variant="outlined"
                      startIcon={downloadingAiPdf ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
                      onClick={handleDownloadAiReportPdf}
                      disabled={downloadingAiPdf}
                      sx={{ ...outlinedButtonSx, mt: 2 }}
                    >
                      {downloadingAiPdf ? 'Preparing PDF...' : 'Download PDF'}
                    </Button>
                    <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
                      This AI-generated report is for astrologer reference only. It should be reviewed, verified, and personalized before sharing with clients.
                    </Alert>
                  </CardContent>
                </Card>
              )}
            </Box>
          )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{
              width: '100%',
              borderRadius: 2,
              border: `1px solid ${BORDER_COLOR}`,
              boxShadow: CARD_SHADOW,
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
      <Footer />
    </>
  );
};

export default AdminDashboard;
